import { FC, useMemo, useState, useEffect } from 'react';
import {
  AddNewPlayerModalProps,
  SelectPlayerOption,
} from './addNewPlayerModal.type';
import Close from '../../assets/close.png';
import { Trans } from 'react-i18next';
import SaveButton from '../button/button';
import { renderPlayerSelection } from '../../pages/sweeps/sweepHelper';
import moment from 'moment';
import ActiveCheck from '../../assets/active_check.png';
import Loading from '../../assets/loading/loading';
import Tooltip from '../tooltip/tooltip';
import { PlayerSummary, POIClassification, PlayerUpdate } from '@inplay/models';
import { usePlayerSummary } from '../../hooks/usePlayerSummary';
import { useNewPlayers } from '../../service/context/newPlayerContext';
import { PlayerPageLimit } from '../../utils/helper.type';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

const AddNewPlayerModal: FC<AddNewPlayerModalProps> = (props) => {
  const { addNewPlayer, updateExistingPlayer } = useNewPlayers();
  const { isTablet } = useResponsiveLayout();
  const generatedRandomID: string = useMemo(
    () => Math.floor(1000 + Math.random() * 9000).toString(),
    [],
  );

  const isEditMode: boolean = Boolean(props.selectedPlayer?.id);
  const [originalPlayerId] = useState<string | null>(
    props.selectedPlayer?.id || null,
  );

  const [selectedOption, setSelectedOption] = useState<SelectPlayerOption>(
    props.selectedPlayer?.selectedOptionType || SelectPlayerOption.Random,
  );

  const [playerSelected, setPlayerSelected] = useState<boolean>(false);
  const [randomId] = useState<string>(() => {
    if (
      props.selectedPlayer?.selectedOptionType === SelectPlayerOption.Random
    ) {
      return props.selectedPlayer.id ?? generatedRandomID;
    }
    return generatedRandomID;
  });

  const [newPlayer, setNewPlayer] = useState<PlayerUpdate>(() => {
    if (props.selectedPlayer?.id) {
      return { ...props.selectedPlayer };
    }
    return {
      id: randomId,
      bodyImage: '',
      description: '',
      presentDuringSweep: true,
      poiClassification: POIClassification.NON_POI,
      consecutiveSweepsCount: 0,
      consecutiveSweepsFlag: false,
      startTime: undefined,
      endTime: undefined,
      selectedOptionType: SelectPlayerOption.Random,
    };
  });

  useEffect(() => {
    if (
      selectedOption === SelectPlayerOption.Existing &&
      props.selectedPlayer?.id
    ) {
      setPlayerSelected(true);
    }
  }, [selectedOption, props.selectedPlayer]);

  const handleOptionChange = (playerOption: SelectPlayerOption) => {
    setSelectedOption(playerOption);

    if (playerOption === SelectPlayerOption.Random) {
      setNewPlayer((previousPlayer) => ({
        ...previousPlayer,
        id: randomId,
        selectedOptionType: SelectPlayerOption.Random,
        bodyImage: '',
        description: '',
        startTime: undefined,
        endTime: undefined,
      }));
      setPlayerSelected(false);
    } else if (playerOption === SelectPlayerOption.Existing) {
      const existingId = props.selectedPlayer?.id || '';
      setNewPlayer((previousPlayer) => ({
        ...previousPlayer,
        id: existingId,
        selectedOptionType: SelectPlayerOption.Existing,
        bodyImage: '',
        description: '',
        startTime: undefined,
        endTime: undefined,
      }));
      setPlayerSelected(!!existingId);
    }
  };

  const { playerSummary, playerLoading } = usePlayerSummary(
    [props.selectedSweep?.venueBranchId ?? ''],
    moment(props.selectedSweep?.venueBranchOpeningTime)
      .unix()
      .toString(),
    moment(props.selectedSweep?.venueBranchClosingTime)
      .unix()
      .toString(),
    0,
    PlayerPageLimit.LIMIT,
    false,
  );

  const handlePlayerClick = (player: PlayerSummary) => {
    const isSamePlayerSelected = newPlayer.id === player.playerId;

    if (isSamePlayerSelected) {
      setNewPlayer((previousPlayer) => ({
        ...previousPlayer,
        id: '',
        bodyImage: '',
        selectedOptionType: SelectPlayerOption.Existing,
      }));
      setPlayerSelected(false);
    } else {
      setNewPlayer((previousPlayer) => ({
        ...previousPlayer,
        id: player.playerId,
        description: player.description,
        bodyImage: player.fullImage || '',
        bodyImageId: `players/full_${player.playerId}_${player.firstEventId}.jpeg`,
        selectedOptionType: SelectPlayerOption.Existing,
      }));
      setPlayerSelected(true);
    }
  };

  const handleSavePlayer = () => {
    const isPlayerIdChanged =
      isEditMode && originalPlayerId && originalPlayerId !== newPlayer.id;

    if (newPlayer) {
      console.log(newPlayer);
    }

    const updatedPlayer: PlayerUpdate & {
      selectedOptionType: SelectPlayerOption;
    } = {
      ...newPlayer,
      id: isEditMode ? newPlayer.id : newPlayer.id || generatedRandomID,
      selectedOptionType: selectedOption,
      presentDuringSweep: true,
      poiClassification:
        newPlayer.poiClassification || POIClassification.NON_POI,
      consecutiveSweepsCount: newPlayer.consecutiveSweepsCount || 0,
      consecutiveSweepsFlag: newPlayer.consecutiveSweepsFlag || false,
      startTime: isPlayerIdChanged
        ? undefined
        : newPlayer.startTime
          ? moment(newPlayer.startTime).unix()
          : undefined,

      endTime: isPlayerIdChanged
        ? undefined
        : newPlayer.endTime
          ? moment(newPlayer.endTime).unix()
          : undefined,
      description: isPlayerIdChanged ? '' : newPlayer.description || '',
      bodyImage: newPlayer.bodyImage || '',
    };

    if (isEditMode && originalPlayerId) {
      updateExistingPlayer(originalPlayerId, updatedPlayer);
    } else {
      addNewPlayer(updatedPlayer);
    }

    props.onCloseSelectPlayerModal();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div
        className={`bg-white  flex flex-col relative overflow-hidden rounded-lg px-10 ${
          isTablet
            ? 'h-[100vh] w-[100vw]'
            : 'w-full h-full max-w-[calc(100%-25rem)] max-h-[calc(100%-5rem)]'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 pt-10 pb-8 flex justify-between items-start">
          <span className="text-xl text-[#1A1A1A]">
            <Trans i18nKey={'sweeps.selectPlayers.selectPlayer'} />
          </span>
          <div className="flex items-end gap-2">
            <img
              onClick={props.onCloseSelectPlayerModal}
              src={Close}
              alt="Close"
              className="w-5 h-5 cursor-pointer"
            />
          </div>
        </div>

        <div className="text-[#717171] text-base pb-8">
          <Trans i18nKey={'sweeps.selectPlayers.autoGeneratedText'} />
        </div>

        {renderPlayerSelection(selectedOption, handleOptionChange, randomId)}

        <div className="flex-1 overflow-auto pt-6">
          {playerLoading ? (
            <div className="flex justify-center items-center h-96">
              <Loading />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 place-items-center">
              {playerSummary?.map((gamingPlayer: PlayerSummary) => {
                const isDisabled: boolean =
                  selectedOption === SelectPlayerOption.Random;
                const isSelected: boolean =
                  newPlayer.id === gamingPlayer.playerId;

                return (
                  <div
                    key={gamingPlayer.playerId}
                    onClick={() => handlePlayerClick(gamingPlayer)}
                    className={`relative rounded-lg overflow-hidden flex flex-col items-center cursor-pointer ${
                      isDisabled ? 'opacity-70 pointer-events-none' : ''
                    }`}
                  >
                    <div
                      className={`relative flex items-center justify-center rounded-2xl w-40 h-[190px] transition-all duration-300 border-[3px] ${
                        isSelected ? 'border-[#561BDC]' : 'border-[#E6E6E6]'
                      }`}
                    >
                      <img
                        src={gamingPlayer.fullImage || ''}
                        alt=""
                        className="w-[155px] h-[185px] object-fill rounded-2xl"
                      />
                      {isSelected && (
                        <div className="absolute top-2 right-2 bg-[#6D28D9] rounded-full p-[2px]">
                          <img
                            src={ActiveCheck}
                            alt="Active"
                            className="w-4 h-4"
                          />
                        </div>
                      )}
                    </div>
                    <Tooltip tooltipContent={gamingPlayer.playerId}>
                      <p className="text-sm text-[#1A1A1A] text-center w-[100px] break-words sm:truncate sm:overflow-hidden sm:text-ellipsis">
                        {gamingPlayer.playerId.slice(0, 5)}
                      </p>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          className="sticky bottom-0 z-10 p-4 flex justify-center gap-2 bg-white"
          style={{ boxShadow: '0px -4px 30px #2F41A50F' }}
        >
          <SaveButton
            onClick={handleSavePlayer}
            disabled={
              playerLoading ||
              (selectedOption === SelectPlayerOption.Existing &&
                !playerSelected)
            }
            className={`py-2.5 rounded-full border-[#D8D8D8] ${
              isTablet ? 'w-[50vw] text-lg' : 'w-60 text-sm'
            }`}
          >
            <Trans
              i18nKey={
                isEditMode ? 'userManagement.update' : 'userManagement.save'
              }
            />
          </SaveButton>
        </div>
      </div>
    </div>
  );
};

export default AddNewPlayerModal;
